/**
 * `sequenceHolds` — the release latches a held roll waits on (`chained-resolution-sequence`
 * Phase 3, `decisions/0210` items 3-5).
 *
 * A hold is a pause in a chained resolution's playback right before a high-stakes roll (the Elite
 * Cataphract's structure check). Every client's board pauses there under the stakes banner; the GM
 * or the rolling unit's controller presses Roll it, the room re-broadcasts the release to every
 * socket, and each client's latch for that hold opens. The dice were drawn on the server already;
 * only the reveal waits.
 *
 * Like `resolutionThrows`, the latch is a rendezvous by key and order-agnostic: a release that
 * arrives before this client's board reaches the hold (a client running behind, or the GM's
 * auto-hold-off pre-release) leaves the latch open, so the board passes straight through. There is
 * no timer. The one automatic release is no GM on the board ({@link releaseAllHolds}), so a
 * GM who closed COMBAT.app or dropped cannot strand the table.
 *
 * Where a resolution holds is the room's call (`chained-resolution-sequence` 4.4): it stamps each
 * resolution with its holds, so every client cuts the same ones.
 */

/** How many holds to remember. Bounded like `resolutionThrows`; a waiter already holds its promise. */
const MAX_TRACKED = 32;

type Latch = { open: Promise<void>; release: () => void; released: boolean };

const latches = new Map<string, Latch>();

function latchFor(key: string): Latch {
  const existing = latches.get(key);
  if (existing !== undefined) return existing;
  let resolve!: () => void;
  const latch: Latch = {
    open: new Promise<void>((r) => {
      resolve = r;
    }),
    released: false,
    release: () => {
      latch.released = true;
      resolve();
    },
  };
  latches.set(key, latch);
  while (latches.size > MAX_TRACKED) {
    const oldest = latches.keys().next();
    if (oldest.done === true) break;
    latches.delete(oldest.value);
  }
  return latch;
}

/** Open the latch for `key`: a waiter passes now, and a later one passes at once. */
export function releaseHold(key: string): void {
  latchFor(key).release();
}

/** Wait until the hold `key` is released. Resolves at once if it already was. */
export function awaitHoldRelease(key: string): Promise<void> {
  return latchFor(key).open;
}

/**
 * Wait until the hold `key` opens: at once while no GM is on the board ({@link holdsAutoRelease}),
 * else on its release. What the board's hold beat and the COMMS card both wait on.
 */
export function holdOpens(key: string): Promise<void> {
  return holdsAutoRelease() ? Promise.resolve() : awaitHoldRelease(key);
}

/** Whether the hold `key` has been released. */
export function isHoldReleased(key: string): boolean {
  return latches.get(key)?.released ?? false;
}

/**
 * Open every hold this client is waiting on, and every hold it will reach for resolutions already
 * received: the GM left the game, and nobody else may be able to release (`decisions/0210` item 4).
 */
export function releaseAllHolds(): void {
  for (const latch of latches.values()) latch.release();
  gmAway = true;
}

/**
 * Whether the GM is away (set by {@link releaseAllHolds}, cleared by {@link noteGmPresent}): while
 * it is, a hold this client reaches opens at once.
 */
let gmAway = false;

/** The GM is connected again: holds wait for a release as normal. */
export function noteGmPresent(): void {
  gmAway = false;
}

/** Whether a hold should open the moment it is reached, because no GM is connected to release it. */
export function holdsAutoRelease(): boolean {
  return gmAway;
}

/** Forget every latch — test isolation only; the module is a process-wide singleton. */
export function resetSequenceHolds(): void {
  latches.clear();
  gmAway = false;
}

